import { useState, type ReactNode } from "react";
import { Check, ExternalLink, Lightbulb, Plus, Sparkles } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  NumberStepper,
  PageLoader,
  Segmented,
  TextField,
  Textarea,
  Toggle,
  cx,
} from "@/components/ui";
import { IconPicker, KidChips, Points } from "@/components/bits";
import { api } from "@/lib/api";
import { useAction, useStore } from "@/lib/queries";
import { useSession } from "@/lib/session";
import type { AdviceResponse, StoreCategory, StoreIdeaDTO, StoreItemDTO } from "@shared/types";
import { AdvisorCard, AdvisorSource, AdvisorThinking, ConfirmDelete, RewardEquivalents, useAdvice } from "./Rewards";

type Draft = Pick<StoreItemDTO, "name" | "description" | "icon" | "category" | "price" | "externalUrl" | "stock" | "kidIds" | "active"> & {
  id?: string;
};

const empty: Draft = {
  name: "",
  description: null,
  icon: "🎁",
  category: "item",
  price: 50,
  externalUrl: null,
  stock: null,
  kidIds: [],
  active: true,
};

const CATEGORIES: { value: StoreCategory; label: string; icon: string; hint: string; tone: "coin" | "violet" | "brand" | "warn" }[] = [
  { value: "item", label: "Item", icon: "🧸", hint: "Something to keep", tone: "coin" },
  { value: "digital", label: "Digital", icon: "🎮", hint: "Games, apps, in-game", tone: "violet" },
  { value: "experience", label: "Experience", icon: "🎟️", hint: "An outing or activity", tone: "brand" },
  { value: "privilege", label: "Privilege", icon: "👑", hint: "Pick dinner, stay up late", tone: "warn" },
];
const categoryOf = (c: StoreCategory) => CATEGORIES.find((x) => x.value === c) ?? CATEGORIES[0];

const STORE_ICONS = [
  "🎁", "🧸", "🎮", "📱", "🎧", "🍦", "🍕", "🍿", "🍩", "🎬", "🎳", "🏊", "🧩", "🎨", "⚽", "🛹",
  "📚", "🎟️", "👑", "🌙", "🎤", "🛍️", "🏕️", "🎡", "🎲", "🚲", "🧁", "⭐",
];

const isUrl = (v: string) => /^https?:\/\/\S+$/i.test(v.trim());

function toDraft(i: StoreItemDTO): Draft {
  return {
    id: i.id,
    name: i.name,
    description: i.description,
    icon: i.icon,
    category: i.category,
    price: i.price,
    externalUrl: i.externalUrl,
    stock: i.stock,
    kidIds: i.kidIds,
    active: i.active,
  };
}

function toBody(d: Draft) {
  return {
    name: d.name.trim(),
    description: d.description?.trim() || null,
    icon: d.icon,
    category: d.category,
    price: Math.round(d.price),
    externalUrl: d.externalUrl?.trim() || null,
    stock: d.stock,
    kidIds: d.kidIds,
    active: d.active,
  };
}

function CategoryBadge({ category }: { category: StoreCategory }) {
  const c = categoryOf(category);
  return <Badge tone={c.tone}>{c.label}</Badge>;
}

function CategoryPicker({ value, onChange }: { value: StoreCategory; onChange: (c: StoreCategory) => void }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup">
      {CATEGORIES.map((c) => (
        <button
          key={c.value}
          type="button"
          role="radio"
          aria-checked={value === c.value}
          onClick={() => onChange(c.value)}
          className={cx(
            "flex flex-col items-start rounded-2xl px-3 py-2.5 text-left transition",
            value === c.value ? "bg-brand-soft ring-2 ring-brand" : "bg-sunken ring-1 ring-line hover:ring-brand/40",
          )}
        >
          <span className="text-xl">{c.icon}</span>
          <span className="text-sm font-extrabold">{c.label}</span>
          <span className="text-xs text-muted">{c.hint}</span>
        </button>
      ))}
    </div>
  );
}

function StoreForm({ draft, setDraft }: { draft: Draft; setDraft: (d: Draft) => void }) {
  const { kids, family } = useSession();
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const url = draft.externalUrl ?? "";
  const icons = STORE_ICONS.includes(draft.icon) ? STORE_ICONS : [draft.icon, ...STORE_ICONS];
  return (
    <div className="space-y-5">
      <TextField
        label="Name"
        placeholder="Pick the movie for movie night"
        value={draft.name}
        maxLength={80}
        onChange={(e) => set({ name: e.target.value })}
      />
      <Field label="Icon">
        <IconPicker value={draft.icon} onChange={(icon) => set({ icon })} choices={icons} />
      </Field>
      <Field label="Kind">
        <CategoryPicker value={draft.category} onChange={(category) => set({ category })} />
      </Field>
      <Field label="Price">
        <NumberStepper value={draft.price} onChange={(price) => set({ price })} min={1} max={1_000_000} step={5} suffix={family.currencyIcon} />
        <RewardEquivalents points={draft.price} className="mt-2" />
      </Field>
      <Field label="Description (optional)">
        <Textarea
          placeholder="What exactly do they get?"
          value={draft.description ?? ""}
          maxLength={500}
          onChange={(e) => set({ description: e.target.value || null })}
        />
      </Field>
      <TextField
        label="Where to buy it (optional)"
        type="url"
        inputMode="url"
        placeholder="https://www.amazon.com/…"
        value={url}
        maxLength={2000}
        onChange={(e) => set({ externalUrl: e.target.value || null })}
        error={url.trim() && !isUrl(url) ? "Links start with http:// or https://" : null}
        hint="Only parents see this link — purchases always need your approval."
      />
      <Field label="Stock" hint={draft.stock === null ? "Kids can buy it as often as they like." : "Each approved purchase uses one. At zero it shows as sold out."}>
        <Segmented
          value={draft.stock === null ? "unlimited" : "limited"}
          onChange={(v) => set({ stock: v === "unlimited" ? null : (draft.stock ?? 1) })}
          options={[
            { value: "unlimited", label: "Unlimited" },
            { value: "limited", label: "Limited" },
          ]}
        />
        {draft.stock !== null && (
          <div className="pt-2">
            <NumberStepper value={draft.stock} onChange={(stock) => set({ stock: Math.round(stock) })} min={0} max={10_000} suffix="left" />
          </div>
        )}
      </Field>
      <Field label="Who can buy it">
        <KidChips kids={kids} value={draft.kidIds} onChange={(kidIds) => set({ kidIds })} allLabel="Every kid" />
      </Field>
      <Toggle
        checked={draft.active}
        onChange={(active) => set({ active })}
        label="Show in the store"
        description="Hidden items stay here for later."
      />
    </div>
  );
}

type Idea = StoreIdeaDTO & { selected: boolean };

function StoreDesigner({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { family, memberById } = useSession();
  const [focus, setFocus] = useState("");
  const [budget, setBudget] = useState("");
  const [count, setCount] = useState(8);
  const [result, setResult] = useState<{ source: "built-in"; note?: string; ideas: Idea[] } | null>(null);

  const generate = useAdvice(
    () => {
      const weeklyBudget = Number(budget);
      return api.post<AdviceResponse<StoreIdeaDTO[]>>("/advisor/store-ideas", {
        focus: focus.trim() || undefined,
        weeklyBudget: budget.trim() && weeklyBudget > 0 ? Math.min(weeklyBudget, 10_000) : undefined,
        count,
      });
    },
    (r) => setResult({ source: r.source, note: r.note, ideas: r.items.map((i) => ({ ...i, selected: true })) }),
  );
  const add = useAction(
    (ideas: Idea[]) =>
      api.post<{ items: StoreItemDTO[] }>("/store/bulk", {
        items: ideas.map((i) => ({
          name: i.name,
          description: i.description || null,
          icon: i.icon,
          category: i.category,
          price: Math.max(1, Math.round(i.price)),
        })),
      }),
    {
      success: (r) => `Added ${r.items.length} to your store 🎉`,
      onSuccess: () => {
        setResult(null);
        onClose();
      },
    },
  );

  const selected = result?.ideas.filter((i) => i.selected) ?? [];
  const update = (index: number, patch: Partial<Idea>) =>
    setResult((r) => (r ? { ...r, ideas: r.ideas.map((i, n) => (n === index ? { ...i, ...patch } : i)) } : r));
  const allSelected = !!result && selected.length === result.ideas.length;

  const footer: ReactNode = generate.isPending ? undefined : result ? (
    <div className="flex gap-2">
      <Button variant="secondary" onClick={() => setResult(null)} className="shrink-0">
        Start over
      </Button>
      <Button block onClick={() => add.mutate(selected)} loading={add.isPending} disabled={!selected.length}>
        {selected.length ? `Add ${selected.length} to store` : "Pick some ideas"}
      </Button>
    </div>
  ) : (
    <Button block onClick={() => generate.mutate()}>
      <Sparkles className="size-4" /> Get ideas
    </Button>
  );

  return (
    <Sheet open={open} onClose={onClose} title="Design my store" size="lg" footer={footer}>
      {generate.isPending ? (
        <AdvisorThinking title="Dreaming up rewards your kids will love…" />
      ) : !result ? (
        <div className="space-y-5">
          <p className="text-sm text-muted">
            Suggestions use your kids' ages and interests to propose meaningful rewards and fair prices, using built-in rules (no AI). They never
            look at what happens day to day.
          </p>
          <TextField
            label="Anything to focus on? (optional)"
            placeholder="More experiences, fewer sweets"
            value={focus}
            maxLength={300}
            onChange={(e) => setFocus(e.target.value)}
          />
          <TextField
            label="Weekly budget in $ (optional)"
            type="number"
            inputMode="decimal"
            min={0}
            placeholder="e.g. 20"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            hint="Roughly what you'd spend on rewards each week, across all kids."
          />
          <Field label="How many ideas?">
            <NumberStepper value={count} onChange={(n) => setCount(Math.round(n))} min={3} max={15} />
          </Field>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <AdvisorSource source={result.source} note={result.note} />
            <button
              type="button"
              className="text-sm font-bold text-brand"
              onClick={() => setResult({ ...result, ideas: result.ideas.map((i) => ({ ...i, selected: !allSelected })) })}
            >
              {allSelected ? "Select none" : "Select all"}
            </button>
          </div>
          {result.ideas.length === 0 && <p className="py-6 text-center text-muted">No ideas this time — try a different focus.</p>}
          {result.ideas.map((idea, index) => {
            const forNames = idea.forKids.map((id) => memberById(id)?.name).filter(Boolean);
            return (
              <div
                key={`${idea.name}-${index}`}
                className={cx(
                  "rounded-3xl p-3 transition",
                  idea.selected ? "bg-card shadow-card ring-2 ring-brand" : "bg-sunken ring-1 ring-line",
                )}
              >
                <button
                  type="button"
                  aria-pressed={idea.selected}
                  onClick={() => update(index, { selected: !idea.selected })}
                  className="flex w-full items-start gap-3 text-left"
                >
                  <span
                    className={cx(
                      "mt-1 flex size-6 shrink-0 items-center justify-center rounded-lg transition",
                      idea.selected ? "bg-brand text-on-brand" : "bg-card ring-1 ring-line",
                    )}
                  >
                    {idea.selected && <Check className="size-4" strokeWidth={3} />}
                  </span>
                  <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-sunken text-2xl">{idea.icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="font-extrabold">{idea.name}</span>
                      <CategoryBadge category={idea.category} />
                    </span>
                    {idea.description && <span className="mt-0.5 block text-sm text-muted">{idea.description}</span>}
                    <span className="mt-1 block text-xs font-bold text-muted">
                      For {forNames.length ? forNames.join(", ") : "everyone"}
                    </span>
                  </span>
                </button>
                {idea.rationale && (
                  <p className="mt-2 flex gap-1.5 pl-9 text-xs text-muted">
                    <Lightbulb className="mt-0.5 size-3.5 shrink-0 text-violet" />
                    {idea.rationale}
                  </p>
                )}
                {idea.selected && (
                  <div className="mt-3 pl-9">
                    <NumberStepper
                      value={idea.price}
                      onChange={(price) => update(index, { price })}
                      min={1}
                      max={1_000_000}
                      step={5}
                      suffix={family.currencyIcon}
                    />
                    <RewardEquivalents points={idea.price} className="mt-2" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}

export function StoreEditor() {
  const { family, memberById } = useSession();
  const { data, isLoading } = useStore();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [designerOpen, setDesignerOpen] = useState(false);
  const [filter, setFilter] = useState<StoreCategory | "all">("all");

  const save = useAction(
    (d: Draft) => (d.id ? api.patch(`/store/${d.id}`, toBody(d)) : api.post("/store", toBody(d))),
    { success: "Saved", onSuccess: () => setDraft(null) },
  );
  const remove = useAction((id: string) => api.del(`/store/${id}`), { success: "Removed", onSuccess: () => setDraft(null) });

  if (isLoading || !data) return <PageLoader />;

  const items = [...data.items].sort((a, b) => Number(b.active) - Number(a.active));
  const categories = CATEGORIES.filter((c) => items.some((i) => i.category === c.value));
  const activeFilter = categories.some((c) => c.value === filter) ? filter : "all";
  const shown = activeFilter === "all" ? items : items.filter((i) => i.category === activeFilter);
  const urlInvalid = !!draft?.externalUrl?.trim() && !isUrl(draft.externalUrl);
  const canSave = !!draft?.name.trim() && draft.price >= 1 && !urlInvalid;

  return (
    <div>
      <PageHeader
        title="Store"
        subtitle={`What kids can buy with ${family.currencyName}`}
        back="/parent/plan"
        actions={
          <Button size="sm" onClick={() => setDraft({ ...empty })}>
            <Plus className="size-4" /> Add
          </Button>
        }
      />

      <div className="mt-2">
        <AdvisorCard title="✨ Suggest store items" onClick={() => setDesignerOpen(true)}>
          Ideas and fair prices tailored to your kids' ages and interests.
        </AdvisorCard>
      </div>

      {items.length === 0 ? (
        <div className="mt-4">
          <EmptyState icon="🛍️" title="Your store is empty" action={<Button onClick={() => setDraft({ ...empty })}>Add an item</Button>}>
            Add treats, toys, outings and privileges kids can save up for — or let the advisor design a menu for you.
          </EmptyState>
        </div>
      ) : (
        <>
          {categories.length > 1 && items.length > 4 && (
            <div className="no-scrollbar -mx-4 mt-4 flex gap-2 overflow-x-auto px-4">
              {[{ value: "all" as const, label: "All", icon: "" }, ...categories].map((c) => (
                <button
                  key={c.value}
                  type="button"
                  aria-pressed={activeFilter === c.value}
                  onClick={() => setFilter(c.value)}
                  className={cx(
                    "shrink-0 rounded-full px-3 py-1.5 text-sm font-bold transition",
                    activeFilter === c.value ?"bg-brand text-on-brand" : "bg-card text-muted ring-1 ring-line hover:text-ink",
                  )}
                >
                  {c.icon ? `${c.icon} ` : ""}
                  {c.label}
                </button>
              ))}
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {shown.map((item) => {
              const forNames = item.kidIds.map((id) => memberById(id)?.name).filter(Boolean);
              return (
                <Card
                  key={item.id}
                  className={cx("flex cursor-pointer flex-col p-3 transition hover:ring-brand/40", !item.active && "opacity-60")}
                  onClick={() => setDraft(toDraft(item))}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="flex size-14 items-center justify-center rounded-2xl bg-sunken text-3xl">{item.icon}</span>
                    {item.externalUrl && (
                      <span title="Has a purchase link" className="rounded-full bg-sunken p-1.5 text-muted">
                        <ExternalLink className="size-3.5" aria-label="Has a purchase link" />
                      </span>
                    )}
                  </div>
                  <div className="mt-2 line-clamp-2 font-extrabold leading-snug">{item.name}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    <CategoryBadge category={item.category} />
                    {item.stock !== null &&
                      (item.stock <= 0 ? <Badge tone="danger">Sold out</Badge> : <Badge>{item.stock} left</Badge>)}
                    {!item.active && <Badge tone="locked">Hidden</Badge>}
                  </div>
                  {forNames.length > 0 && <div className="mt-1 truncate text-xs text-muted">Only {forNames.join(", ")}</div>}
                  <div className="mt-auto pt-2">
                    <Points value={item.price} family={family} className="text-lg text-coin-ink" />
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      <Sheet
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? "Edit item" : "New store item"}
        footer={
          <div className="flex gap-2">
            {draft?.id && (
              <ConfirmDelete label="Remove" confirmLabel="Remove?" onConfirm={() => draft.id && remove.mutate(draft.id)} loading={remove.isPending} />
            )}
            <Button block onClick={() => draft && save.mutate(draft)} loading={save.isPending} disabled={!canSave}>
              Save
            </Button>
          </div>
        }
      >
        {draft && <StoreForm draft={draft} setDraft={setDraft} />}
      </Sheet>

      <StoreDesigner open={designerOpen} onClose={() => setDesignerOpen(false)} />
    </div>
  );
}
